import { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO 
        title="Terms & Conditions - Trademark Protocols"
        description="Read our Terms & Conditions to understand the terms of use for Trademark Protocols services and website."
        keywords="terms of service, terms and conditions, legal agreement, user agreement"
      />
      
      <Header />
      
      <div className="min-h-screen bg-primary-600 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header */}
          <div className="text-center py-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-primary-600 bg-clip-text text-white">
              Terms & Conditions
            </h1>
            <p className="text-secondary-600 text-lg">
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            <section>
              <p className="text-gray-700 leading-relaxed">
                By accessing the Trademark Protocols website (referred to as the "Site"), you acknowledge and accept the terms and conditions outlined herein, forming an agreement between you and Trademark Protocols (referred to as "we," "us," or "our"). This agreement also incorporates our Privacy Policy as if fully stated here. If you do not agree with these terms, please refrain from using the Site. We offer general information related to trademarks and provide a platform where you can prepare and file trademarks. <strong className="text-primary-600">THIS AGREEMENT INCLUDES A MANDATORY ARBITRATION CLAUSE GOVERNED BY THE FEDERAL ARBITRATION ACT, AND A WAIVER OF CLASS ACTIONS.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Conditions for Use of the Site</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your access to and use of the Site is contingent upon your acceptance and adherence to these terms. By using the Site, you agree that you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Will comply with these Terms of Service;</li>
                <li>Are 18 years of age or older;</li>
                <li>Will not use the Site in any way that violates U.S. laws, including its export and re-export regulations;</li>
                <li>Will not copy or distribute any content from the Site without our prior written consent;</li>
                <li>Will provide accurate details when registering, submitting content, or interacting with the Site;</li>
                <li>Will not permit others to use the Site under your user credentials;</li>
                <li>Are fully responsible for all activity under your account and user credentials;</li>
                <li>Will not use the Site for commercial purposes other than those expressly stated;</li>
                <li>Will not use the Site to collect personal information about others or solicit users or advertisers;</li>
                <li>Are solely responsible for any content you submit to the Site, ensuring you have the rights or licenses to do so, and will not upload any materials infringing on others' copyrights or proprietary rights;</li>
                <li>Grant us and other users a non-exclusive license to use, display, reproduce, distribute, or create derivative works from your submitted materials; and</li>
                <li>Agree that we may remove any of your content and suspend or terminate your account, with or without prior notice.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Content</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to publish any content you provide (referred to as "User-Generated Content" or "UGC"). You agree not to upload or use any UGC that:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Violates the intellectual property rights of others, including copyright, trademarks, or trade secrets;</li>
                <li>Infringes on privacy or publicity rights of any third party;</li>
                <li>Is unlawful, inaccurate, defamatory, discriminatory, obscene, or harassing;</li>
                <li>Encourages criminal activity or civil liability;</li>
                <li>Is deemed inappropriate by us, in our sole discretion; or</li>
                <li>Could harm our company, affiliates, or partners.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We are not obligated to remove UGC violating these terms, but we reserve the right to do so at our discretion, and may take further action such as account termination. We are not liable for any harm caused by UGC.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Behavior</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By using the Site, you agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Engage in or promote illegal activities;</li>
                <li>Attempt to reverse engineer or disrupt the Site's functionality;</li>
                <li>Attempt to access restricted areas of the Site;</li>
                <li>Use the Site for unsolicited email campaigns or spam;</li>
                <li>Use automated tools to scrape or collect data from the Site;</li>
                <li>Interfere with the proper functioning of the Site;</li>
                <li>Impersonate another user.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Links to Third-Party Sites</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Site may contain links to third-party websites not controlled by us. We assume no responsibility for the content or practices of these external sites. Linking to these sites does not imply endorsement by us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify or discontinue the Site or any services provided without prior notice. Additionally, we may terminate this agreement for any reason, with or without notice. If we terminate your access due to violations of these terms, you will immediately lose access to the Site. The clauses related to warranties, liabilities, indemnity, dispute resolution, and your content will survive the termination of this agreement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Trademark Search Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you use our search services, we employ reasonable methods to identify potential matches to the trademarks you wish to use. However, we cannot guarantee that your trademark will be approved by the USPTO or that it will not face challenges, even with our search services.
              </p>
              <div className="bg-secondary-100 border-l-4 border-primary-600 p-4 rounded">
                <h3 className="font-bold text-gray-900 mb-2">Trademark Search Scope:</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li><strong>Federal Search:</strong> Reviews USPTO database including direct matches, phonetic similarities, and visual design similarities.</li>
                  <li><strong>Federal, State & Common Law Search:</strong> Extends to all 50 states, business registry, and domain name databases.</li>
                  <li><strong>Global Search:</strong> Includes USPTO, state databases, business registries, domain databases, WIPO database, and European Community database.</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription and Auto-Renewals</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you signed up for one or more subscription services through the Website, your license to the Services is valid during the period your subscription remains in good standing. Unless otherwise provided, your Subscription Term is on an automatic renewal and you will be required to pay, through the credit card provided by you, for the next applicable Subscription Term at the beginning of the period.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4 font-semibold">
                YOU ARE RESPONSIBLE FOR SUBSCRIPTION FEES UNTIL YOU CANCEL YOUR SUBSCRIPTION OR IT IS OTHERWISE TERMINATED.
              </p>
              <p className="text-gray-700 leading-relaxed">
                AT THE END OF A TRIAL OR PROMOTIONAL MEMBERSHIP, UNLESS OTHERWISE STATED IN THE OFFER OR YOU CANCEL, YOUR SUBSCRIPTION WILL RENEW AUTOMATICALLY AND YOUR CREDIT CARD WILL BE CHARGED TO BEGIN YOUR INITIAL SUBSCRIPTION TERM.
              </p>
            </section>

            <section className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Disclaimers of Warranty and Limitations of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4 uppercase text-sm font-semibold">
                WE PROVIDE THE WEBSITE AND THE RELATED SERVICES "AS IS", "WHERE IS", AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ANY AND ALL EXPRESS OR IMPLIED WARRANTIES THAT THE WEBSITE AND SERVICES ARE MERCHANTABLE, OF SATISFACTORY QUALITY, ACCURATE, TIMELY OR FIT FOR A PARTICULAR PURPOSE OR NEED.
              </p>
              <p className="text-gray-700 leading-relaxed uppercase text-sm font-semibold">
                WE SHALL NOT BE LIABLE FOR ANY INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES ARISING FROM YOUR USE OF, INABILITY TO USE, OR RELIANCE UPON THE WEBSITE IN ANY WAY.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnity</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to defend, indemnify and hold harmless us, our officers, directors, representatives, employees and agents from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to reasonable attorney's fees) arising from: (i) your violation of any term of these Terms of Service; (ii) your violation of any third party right, including without limitation any copyright, property, privacy right, or any and all intangible or intellectual property rights; or (iii) any claim that any of your UGC causes damage to a third party.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Dispute Resolution and Arbitration</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Any Dispute shall be FINALLY and EXCLUSIVELY resolved by binding individual arbitration conducted by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules. This arbitration agreement is made pursuant to a transaction involving interstate commerce, and shall be governed by the Federal Arbitration Act.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded">
                <p className="text-gray-700 leading-relaxed font-semibold uppercase text-sm">
                  ARBITRATION OF YOUR CLAIM IS MANDATORY AND BINDING. NEITHER PARTY WILL HAVE THE RIGHT TO LITIGATE THAT CLAIM THROUGH A COURT. NO CLASS ACTION, OR OTHER REPRESENTATIVE ACTION OR PRIVATE ATTORNEY GENERAL ACTION OR JOINDER OR CONSOLIDATION OF ANY CLAIM WITH A CLAIM OF ANOTHER PERSON SHALL BE ALLOWABLE.
                </p>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>RIGHT TO OPT-OUT:</strong> You may opt-out of arbitration by notifying us of your decision to reject the arbitration clause in writing within 30 days of accepting this Agreement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Refund Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                All packages include attorney-conducted searches and are non-refundable. Once a search and clearance report is issued, payment will be automatically processed within 24-48 hours if we do not receive a response from you via email or phone.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Copyright Notice</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you believe your copyright has been violated, please send a notification in accordance with the Digital Millennium Copyright Act (17 U.S.C. § 512(c)(3)) with the subject line: "Copyright Notice."
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Modifications to the Agreement</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain the right to modify or discontinue any of these terms and/or the Privacy Policy at any time, as we deem necessary. We may, but are not required to, notify you of significant changes through email or by posting a notice on the Website. Continued use of the Website after such notice shall be deemed as acceptance of the changes.
              </p>
            </section>

            <section className="bg-primary-100 border-l-4 border-primary-600 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions or concerns about these Terms & Conditions, please contact us at:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Company:</strong> Trademark Protocols</p>
                <p><strong>Address:</strong> 600 W Broadway Suite 700, San Diego, CA 92101</p>
                <p><strong>Phone:</strong> +1 (844) 773 6722</p>
              </div>
            </section>

            <section className="border-t pt-6">
              <p className="text-secondary-600 italic">
                By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions.
              </p>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default TermsOfService;
